import { defineStore } from 'pinia';
import type { DesiredHome, CurrentHome, AdditionalHomeDetails, RegistrationInfo, ParsedData } from '@/models/Registration';

// Type Definitions
export interface RegistrationState {
  submitLoading: Promise<object> | null;
  registrationError: boolean;
  csrfToken: string | null;
  minSteps: number;
  maxSteps: number;
  numberOfFoundMatches: number;
  step: number;
  isNextButtonClicked: boolean;
  isRegistrationButtonClicked: boolean;
  desiredHome: DesiredHome;
  currentHome: CurrentHome;
  additionalHomeDetails: AdditionalHomeDetails;
  registrationInfo: RegistrationInfo;
}

export interface selectedCities {
  id: number;
  areaCount: number;
  matchRate: number;
  name: string;
  regionId: number;
  areas: object[];
}

// Store Definition
export const useRegistrationStore = defineStore('registration', () => {
  const { systemAPI, authAPI } = useBackendRequests();

  const state = reactive<RegistrationState>({
    submitLoading: null,
    registrationError: false,
    csrfToken: '123',
    minSteps: 1,
    maxSteps: 4,
    numberOfFoundMatches: 0,
    step: 1,
    isNextButtonClicked: false,
    isRegistrationButtonClicked: false,
    desiredHome: {
      selectedCities: [],
      locationIds: { regions: [], cities: [], areas: [] },
      type: 1,
      rentMax: null,
      sizeMin: null,
      roomsMin: 1,
    },
    currentHome: {
      cityName: '',
      geoId: null,
      type: 1,
      rent: null,
      size: null,
      rooms: 1,
    },
    additionalHomeDetails: {
      title: null,
      description: null,
    },
    registrationInfo: {
      firstName: null,
      lastName: null,
      email: null,
      password: null,
      newsletterFeedback: false,
      termsFeedback: false,
    },
  });

  let parsedData: ParsedData | null = null;

  function loadLocalStorage() {
    let savedData = null;
    let expirationDate = null;
    const currentDate = new Date();
    if (typeof window !== 'undefined') {
      savedData = localStorage.getItem('registrationData');
      expirationDate = localStorage.getItem('expirationDate');
      if (savedData && expirationDate && new Date(expirationDate) > currentDate) {
        parsedData = JSON.parse(savedData);

        state.desiredHome = {
          selectedCities: [],
          locationIds: { regions: [], cities: [], areas: [] },
          type: 1,
          rentMax: null,
          sizeMin: null,
          roomsMin: 1,
          ...(parsedData && parsedData.desiredHome ? parsedData.desiredHome : {}),
        };
        state.currentHome = {
          cityName: '',
          geoId: null,
          type: 1,
          rent: null,
          size: null,
          rooms: 1,
          ...(parsedData && parsedData.currentHome ? parsedData.currentHome : {}),
        };
        state.additionalHomeDetails = {
          title: null,
          description: null,
          ...(parsedData && parsedData.additionalHomeDetails ? parsedData.additionalHomeDetails : {}),
        };
        state.registrationInfo = {
          firstName: null,
          lastName: null,
          email: null,
          password: null,
          newsletterFeedback: false,
          termsFeedback: false,
          ...(parsedData && parsedData.registrationInfo ? parsedData.registrationInfo : {}),
        };
      }
    }
  }

  function saveToLocalStorage() {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 30);
    const dataToSave = {
      currentHome: state.currentHome,
      desiredHome: state.desiredHome,
      additionalHomeDetails: state.additionalHomeDetails,
      registrationInfo: state.registrationInfo as RegistrationInfo,
    };
    if (typeof window !== 'undefined') {
      localStorage.setItem('registrationData', JSON.stringify(dataToSave));
      localStorage.setItem('expirationDate', expirationDate.toISOString());
    }
  }

  function triggerNextButtonClick() {
    state.isNextButtonClicked = true;
  }

  function previousStep() {
    if (state.step === state.minSteps) return;
    state.step--;
  }

  function nextStep() {
    state.step++;
    saveToLocalStorage();
  }

  async function submit() {
    state.submitLoading = systemAPI
      .register({
        search: {
          locationIds: state.desiredHome.locationIds,
          type: state.desiredHome.type,
          rentMax: state.desiredHome.rentMax,
          sizeMin: state.desiredHome.sizeMin,
          roomsMin: state.desiredHome.roomsMin,
        },
        housing: {
          geoId: state.currentHome.geoId,
          type: state.currentHome.type,
          rent: state.currentHome.rent,
          size: state.currentHome.size,
          rooms: state.currentHome.rooms,
          title: state.additionalHomeDetails.title,
          description: state.additionalHomeDetails.description,
        },
        user: {
          firstName: state.registrationInfo.firstName,
          lastName: state.registrationInfo.lastName,
          email: state.registrationInfo.email,
          password: state.registrationInfo.password,
          newsletter: state.registrationInfo.newsletterFeedback,
          terms: state.registrationInfo.termsFeedback,
        },
        csrf: state.csrfToken,
      })
      .finally(() => {
        state.submitLoading = null;
      });
    return await state.submitLoading;
  }

  async function preserveRegistrationFormData(
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    newsletterFeedback: boolean,
    termsFeedback: boolean
  ) {
    const userInfo = [
      { key: 'firstName', value: firstName },
      { key: 'lastName', value: lastName },
      { key: 'email', value: email },
      { key: 'password', value: password },
      { key: 'newsletter', value: newsletterFeedback.toString() },
      { key: 'terms', value: termsFeedback.toString() },
    ];

    const bodyFormData = new FormData();

    userInfo.forEach((item) => {
      if (item.value !== null) {
        bodyFormData.append(`user[${item.key}]`, item.value.toString());
      }
    });

    for (const key in state.desiredHome) {
      if (key === 'selectedCities') continue;
      const value = state.desiredHome[key as keyof DesiredHome];
      if (value !== null) {
        if (key === 'locationIds') {
          Object.entries(value).forEach(([subKey, subValue]) => {
            if (Array.isArray(subValue) && subValue.length > 0) {
              subValue.forEach((id) => {
                bodyFormData.append(`search[${key}][${subKey}]`, id.toString());
              });
            }
          });
        } else {
          bodyFormData.append(`search[${key}]`, value.toString());
        }
      }
    }

    for (const key in state.currentHome) {
      const value = state.currentHome[key as keyof CurrentHome];
      if (value !== null) {
        bodyFormData.append(`housing[${key}]`, value.toString());
      }
    }

    for (const key in state.additionalHomeDetails) {
      const value = state.additionalHomeDetails[key as keyof AdditionalHomeDetails];
      if (value !== null) {
        bodyFormData.append(`housing[${key}]`, value.toString());
      }
    }

    const formDataObject = Object.fromEntries(bodyFormData.entries());
    return authAPI.preserveRegistrationFormData(formDataObject);
  }

  function reset() {
    state.isNextButtonClicked = false;
    state.isRegistrationButtonClicked = false;
    state.csrfToken = null;
    state.submitLoading = null;
    state.desiredHome = {
      selectedCities: [],
      locationIds: { regions: [], cities: [], areas: [] },
      type: 1,
      rentMax: null,
      sizeMin: null,
      roomsMin: 1,
    };
    state.currentHome = {
      cityName: '',
      geoId: null,
      type: 1,
      rent: null,
      size: null,
      rooms: 1,
    };
    state.additionalHomeDetails = {
      title: null,
      description: null,
    };
    state.registrationInfo = {
      firstName: null,
      lastName: null,
      email: null,
      password: null,
      newsletterFeedback: false,
      termsFeedback: false,
    };
  }

  return {
    state,
    loadLocalStorage,
    saveToLocalStorage,
    triggerNextButtonClick,
    nextStep,
    previousStep,
    submit,
    preserveRegistrationFormData,
    reset,
  };
});

export default useRegistrationStore;
